import { EnvironmentEnum, EnvironmentMode } from '@/env/enums/EnvironmentMode';
import EnvironmentTheme, { ThemeOption } from '@/env/services/EnvironmentTheme';
import EnvironmentFeatures from '@/env/services/EnvironmentFeatures';
import { appDomainConfig, AppDomainConfig, AppDomainKey } from '@/env/AppDomain';
import { keys } from 'lodash-es';
import { convertArrayToBooleanRecord } from '@/services/utils/BasicUtils';
import { Currency } from '@/modules/multiForm/shared/MultiformDictionaries';
import { ProductType } from '@/commons/enums/ProductType';
import { Route, RouteConfig } from 'vue-router';
import { AppDomain } from '@/env/enums/Domains';
import { I18N } from '@/services/enumTranslator/I18NDictionary';
import {
  AgreementOrganisation,
  AgreementType,
  IAgreementItemLabel,
  IAgreementItemWithUrl
} from '@/modules/client/views/client-agreements/Agreements';
import { CrmModules } from '@/types/CrmModules';
import processProxy from '@/env/processProxy';

function getEnumKey(value: AppDomain): AppDomainKey {
  const result = Object.entries(AppDomain).find(([key, val,]) => val === value);
  return result![0]! as AppDomainKey;
}

const DOMAINS_WITH_MFA_LOGIN_REDIRECT: AppDomain[] = [
  AppDomain.LENDI_PL,
  AppDomain.NESTO_RO,
];

function resolveDeploymentHost() {
  const host = window.location.hostname;
  if (host === 'localhost' || host.startsWith('127.')) return 'local';
  if (host.endsWith('.vercel.app')) return 'vercel';
  if (host.endsWith('.lendi.pl') || host.endsWith('.lendi.services') || host.endsWith('.imobiliare.ro')) return 'lendi';
  return 'unknown';
}
export default abstract class EnvironmentService {
  public static get Environment() {
    const appDomain: AppDomain = window.__DOMAIN__ as AppDomain;
    const getAppDomainConfig = (): AppDomainConfig => appDomainConfig[getEnumKey(appDomain)];
    const resolveCrossTabRenewLockFlag = (): boolean => {
      const rawValue = processProxy.VUE_APP_CROSS_TAB_RENEW_LOCK;
      if (typeof rawValue === 'boolean') {
        return rawValue;
      }
      if (typeof rawValue === 'string') {
        return rawValue === 'true';
      }

      // Default: enabled everywhere unless explicitly set to false.
      return true;
    };
    return {
      buildDate: processProxy.VUE_APP_BUILD_DATE,
      embedWidgetUrl: processProxy.VUE_APP_EMBED_WIDGET_URL,
      env: processProxy.VUE_APP_ENV,
      mode: processProxy.VUE_APP_MODE,
      isLendi: processProxy.VUE_APP_MODE === EnvironmentMode.LENDI,
      isStandard: processProxy.VUE_APP_MODE === EnvironmentMode.STANDARD,
      isDev: processProxy.VUE_APP_ENV === EnvironmentEnum.DEV,
      isProd: processProxy.VUE_APP_ENV === EnvironmentEnum.PROD,
      isPreprod: processProxy.VUE_APP_ENV === EnvironmentEnum.PREPROD,
      isQaLike: processProxy.VUE_APP_ENV === EnvironmentEnum.PREPROD || processProxy.VUE_APP_VERCEL_ENV === 'preview',
      isPreprodOrDev: [EnvironmentEnum.PREPROD, EnvironmentEnum.DEV,]
        .includes(processProxy.VUE_APP_ENV! as EnvironmentEnum),
      isVercelHosted: resolveDeploymentHost() === 'vercel',
      shouldRedirectLoginToMfa: (): boolean => DOMAINS_WITH_MFA_LOGIN_REDIRECT.includes(appDomain),
      isCrossTabRenewLockEnabled: (): boolean => resolveCrossTabRenewLockFlag(),
      ...EnvironmentFeatures[<EnvironmentEnum>processProxy.VUE_APP_ENV],
      isUserComServiceOne:
        EnvironmentFeatures[<EnvironmentEnum>processProxy.VUE_APP_ENV].isUserComServiceOne &&
        appDomain === AppDomain.LENDI_PL,
      appDomain: appDomain,
      agreementOrganisation: (appDomain === AppDomain.LENDI_PL ? 'Lendi' : 'Imobiliare') as Extract<AgreementOrganisation, 'Imobiliare' | 'Lendi'>,
      currentCurrency: (): Currency => getAppDomainConfig().currency,
      getAppDomainConfig: getAppDomainConfig,
      appDomainFeatures: {
        isDistrictsOn: getAppDomainConfig().administrativeLevels >= 2,
        hasRealEstatesOn: getAppDomainConfig().availableProducts.includes(ProductType.REAL_ESTATE),
      },
      isAppDomain: (): Record<AppDomainKey, boolean> =>
        convertArrayToBooleanRecord<AppDomainKey>(
          keys(AppDomain) as (AppDomainKey)[],
          (key) => AppDomain[key] === appDomain),
      hasExcludedModule: (route: RouteConfig | Route): boolean => {
        return route.meta?.module && getAppDomainConfig().excludedModules.includes(route.meta.module);
      },
      hasAccessToModule: (module: CrmModules): boolean => {
        return !getAppDomainConfig().excludedModules.includes(module);
      },
      getDomainAgreement: (agreement: AgreementType): IAgreementItemLabel => {
        const defaultLangAgreement: IAgreementItemLabel | undefined = getAppDomainConfig()
          .agreements[getAppDomainConfig().defaultSystemLanguage]?.[agreement];

        const currentSystemLangAgreement: IAgreementItemLabel | undefined = getAppDomainConfig()
          .agreements[I18N.systemLanguage]?.[agreement];

        if (!currentSystemLangAgreement?.label?.length && !defaultLangAgreement?.label?.length) {
          console.error(`Agreement ${agreement} not found in config`);
          return { title: 'AGREEMENT_NAME_NOT_FOUND', label: 'AGREEMENT_NAME_NOT_FOUND', };
        } else {
          return currentSystemLangAgreement! || defaultLangAgreement!;
        }
      },
      getGPDRDomainAgreementsWithUrl: (): IAgreementItemWithUrl => {
        const currentSystemLang = I18N.systemLanguage;
        const defualtSystemLang = getAppDomainConfig().defaultSystemLanguage;
        const currentSystemLangGPDRAgreement: IAgreementItemWithUrl | undefined = getAppDomainConfig()
          .gpdrAgreementWithUrl[currentSystemLang];
        const defaultLangGPDRAgreement: IAgreementItemWithUrl = getAppDomainConfig()
          .gpdrAgreementWithUrl[defualtSystemLang]!;
        if (!currentSystemLangGPDRAgreement?.url?.length &&
          !defaultLangGPDRAgreement?.url?.length) {
          console.error('GPDR url not found in config');
          return { title: 'GPDR_URL_NOT_FOUND', url: '', };
        } else if (currentSystemLangGPDRAgreement?.url === defaultLangGPDRAgreement?.url && defualtSystemLang !== currentSystemLang) {
          console.error('There is no GPDR consent for this language, using default one.');
          return currentSystemLangGPDRAgreement;
        } else {
          return currentSystemLangGPDRAgreement || defaultLangGPDRAgreement;
        }
      },
    };
  }

  static get Theme(): ThemeOption {
    return EnvironmentTheme[<EnvironmentMode>processProxy.VUE_APP_MODE];
  }
}
